import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Users, Calculator } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  subscription: any | null;
}

const EditSubscriptionDialog = ({ open, onOpenChange, subscription }: Props) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<any>(null);

  // Sync form when subscription changes
  if (subscription && (!form || form._id !== subscription.id)) {
    setForm({
      _id: subscription.id,
      plan: subscription.plan,
      amount: subscription.amount,
      billing_cycle: subscription.billing_cycle,
      status: subscription.status,
      expires_at: subscription.expires_at ? subscription.expires_at.split("T")[0] : "",
      started_at: subscription.started_at ? subscription.started_at.split("T")[0] : "",
      notes: subscription.notes || "",
      payment_method: subscription.payment_method || "",
      is_trial: subscription.is_trial || false,
      trial_ends_at: subscription.trial_ends_at ? subscription.trial_ends_at.split("T")[0] : "",
      discount_percent: subscription.discount_percent || 0,
      discount_type: subscription.discount_type || "none",
      pricing_model: subscription.pricing_model || "fixed",
      price_per_child: subscription.price_per_child || 10,
      min_charge: subscription.min_charge || 500,
    });
  }

  const nurseryId = subscription?.nursery_id;

  const { data: childrenCount = 0 } = useQuery({
    queryKey: ["edit-sub-children-count", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return 0;
      const { count } = await supabase
        .from("children")
        .select("*", { count: "exact", head: true })
        .eq("nursery_id", nurseryId);
      return count || 0;
    },
    enabled: !!nurseryId && form?.pricing_model === "per_child",
  });

  const perChildAmount = useMemo(() => {
    if (!form || form.pricing_model !== "per_child") return 0;
    const ppc = parseFloat(form.price_per_child) || 0;
    const mc = parseFloat(form.min_charge) || 0;
    return Math.max(childrenCount * ppc, mc);
  }, [form?.pricing_model, form?.price_per_child, form?.min_charge, childrenCount]);

  if (!form) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const finalAmount = form.pricing_model === "per_child" ? perChildAmount : parseFloat(form.amount);

    const { error } = await supabase.from("nursery_subscriptions").update({
      plan: form.plan, amount: finalAmount, billing_cycle: form.billing_cycle,
      status: form.status, expires_at: form.expires_at || null,
      started_at: form.started_at || undefined,
      notes: form.notes || null, payment_method: form.payment_method || null,
      is_trial: form.is_trial, trial_ends_at: form.is_trial ? (form.trial_ends_at || null) : null,
      discount_percent: form.discount_percent, discount_type: form.discount_type,
      pricing_model: form.pricing_model,
      price_per_child: form.pricing_model === "per_child" ? parseFloat(form.price_per_child) || 10 : null,
      min_charge: form.pricing_model === "per_child" ? parseFloat(form.min_charge) || 500 : null,
    } as any).eq("id", subscription.id);
    setLoading(false);
    if (error) { toast.error("خطأ: " + error.message); return; }
    toast.success("تم تحديث الاشتراك");
    queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    onOpenChange(false);
    setForm(null);
  };

  const u = (key: string, val: any) => setForm({ ...form, [key]: val });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setForm(null); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader><DialogTitle>تعديل الاشتراك</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Plan & Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>الخطة</Label>
              <Select value={form.plan} onValueChange={(v) => u("plan", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">مجاني</SelectItem>
                  <SelectItem value="basic">أساسي</SelectItem>
                  <SelectItem value="premium">متقدم</SelectItem>
                  <SelectItem value="enterprise">مؤسسي</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>الحالة</Label>
              <Select value={form.status} onValueChange={(v) => u("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">نشط</SelectItem>
                  <SelectItem value="suspended">معلق</SelectItem>
                  <SelectItem value="expired">منتهي</SelectItem>
                  <SelectItem value="cancelled">ملغي</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Pricing Model */}
          <div>
            <Label>نموذج التسعير</Label>
            <Select value={form.pricing_model} onValueChange={(v) => u("pricing_model", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">ثابت (باقة)</SelectItem>
                <SelectItem value="per_child">حسب عدد الأطفال</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Amount / Per-child fields */}
          {form.pricing_model === "fixed" ? (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>المبلغ (ج.م)</Label>
                <Input type="number" value={form.amount} onChange={(e) => u("amount", e.target.value)} min={0} dir="ltr" />
              </div>
              <div>
                <Label>دورة الفوترة</Label>
                <Select value={form.billing_cycle} onValueChange={(v) => u("billing_cycle", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">شهري</SelectItem>
                    <SelectItem value="yearly">سنوي</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>طريقة الدفع</Label>
                <Select value={form.payment_method || "none"} onValueChange={(v) => u("payment_method", v === "none" ? "" : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">غير محدد</SelectItem>
                    <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                    <SelectItem value="cash">نقدي</SelectItem>
                    <SelectItem value="online">إلكتروني</SelectItem>
                    <SelectItem value="card">بطاقة</SelectItem>
                    <SelectItem value="wallet">محفظة</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>سعر الطفل (ج.م)</Label>
                  <Input type="number" value={form.price_per_child} onChange={(e) => u("price_per_child", e.target.value)} min={0} dir="ltr" />
                </div>
                <div>
                  <Label>الحد الأدنى (ج.م)</Label>
                  <Input type="number" value={form.min_charge} onChange={(e) => u("min_charge", e.target.value)} min={0} dir="ltr" />
                </div>
              </div>
              <div className="rounded-lg border p-3 bg-accent/30 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Calculator className="w-4 h-4" />
                  <span>حساب تلقائي</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-3.5 h-3.5 text-muted-foreground" />
                  <span>{childrenCount} طفل × {form.price_per_child} ج.م = {(childrenCount * (parseFloat(form.price_per_child) || 0)).toLocaleString()} ج.م</span>
                </div>
                {perChildAmount > childrenCount * (parseFloat(form.price_per_child) || 0) && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ تم تطبيق الحد الأدنى ({parseFloat(form.min_charge).toLocaleString()} ج.م)
                  </p>
                )}
                <div className="text-sm font-bold border-t pt-1">
                  المبلغ الشهري: {perChildAmount.toLocaleString()} ج.م
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>دورة الفوترة</Label>
                  <Select value={form.billing_cycle} onValueChange={(v) => u("billing_cycle", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">شهري</SelectItem>
                      <SelectItem value="yearly">سنوي</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>طريقة الدفع</Label>
                  <Select value={form.payment_method || "none"} onValueChange={(v) => u("payment_method", v === "none" ? "" : v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">غير محدد</SelectItem>
                      <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                      <SelectItem value="cash">نقدي</SelectItem>
                      <SelectItem value="online">إلكتروني</SelectItem>
                      <SelectItem value="card">بطاقة</SelectItem>
                      <SelectItem value="wallet">محفظة</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>تاريخ البدء</Label>
              <Input type="date" value={form.started_at} onChange={(e) => u("started_at", e.target.value)} dir="ltr" />
            </div>
            <div>
              <Label>تاريخ الانتهاء</Label>
              <Input type="date" value={form.expires_at} onChange={(e) => u("expires_at", e.target.value)} dir="ltr" />
            </div>
          </div>

          {/* Discount */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>نسبة الخصم %</Label>
              <Input type="number" value={form.discount_percent} onChange={(e) => u("discount_percent", parseFloat(e.target.value) || 0)} min={0} max={100} dir="ltr" />
            </div>
            <div>
              <Label>نوع الخصم</Label>
              <Select value={form.discount_type || "none"} onValueChange={(v) => u("discount_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">بدون</SelectItem>
                  <SelectItem value="annual">سنوي</SelectItem>
                  <SelectItem value="promo">كود ترويجي</SelectItem>
                  <SelectItem value="group">مجموعة</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Trial */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-2">
              <Label>فترة تجريبية</Label>
            </div>
            <Switch checked={form.is_trial} onCheckedChange={(v) => u("is_trial", v)} />
          </div>
          {form.is_trial && (
            <div>
              <Label>انتهاء التجربة</Label>
              <Input type="date" value={form.trial_ends_at} onChange={(e) => u("trial_ends_at", e.target.value)} dir="ltr" />
            </div>
          )}

          <div>
            <Label>ملاحظات</Label>
            <Textarea value={form.notes} onChange={(e) => u("notes", e.target.value)} rows={2} />
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ التعديلات"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditSubscriptionDialog;
